import { useNavigate } from "@tanstack/react-router";
import { LinkExternal01 } from "@untitledui/icons";
import type React from "react";
import { useEffect, useState } from "react";
import { AlertBanner } from "@/components/alert-banner";
import { useLoginTransition } from "@/components/application/login-transition/login-transition";
import { AuthLinkButton, AuthSplitLayout, AuthTitle } from "@/components/auth/auth-split-layout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { PasswordInput } from "@/components/base/input/password-input";
import { useRemoteConfig } from "@/hooks/use-remote-config";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/providers/auth-provider";
import { loginRoute } from "@/router";
import { safeRedirectPath } from "@/utils/safe-redirect";

export const Login = () => {
  // TODO: Form
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { onSignIn } = useAuth();
  const search = loginRoute.useSearch();
  const banner = useRemoteConfig("C-login-banner");
  const { start: startLoginTransition } = useLoginTransition();

  const completeSignIn = async () => {
    await startLoginTransition();
    await onSignIn();
    navigate({ to: safeRedirectPath(search.next) });
  };
  useEffect(() => {
    if (search.email && search.password) {
      navigate({ to: "/auto-login", search: { email: search.email, password: search.password } });
    } else if (search.email) {
      setUsername(search.email);
    }
  }, [search.email, search.password, navigate]);

  const signInErrorMessage = (status: number | undefined, message: string | undefined): string => {
    if (status === 0 || status === 503) return "Service temporarily unavailable. Please try again in a few minutes.";
    if (status === 401 || status === 403) return "Log in failed. Please check your credentials.";
    return message || "Log in failed";
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const email = username.toLowerCase().trim();
    try {
      const { data, error: signInError } = await authClient.signIn.email({ email, password });
      if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
        navigate({ to: "/login/two-factor", search: { next: search.next } });
        return;
      }
      if (signInError) {
        setError(signInErrorMessage(signInError.status, signInError.message));
        return;
      }
      await completeSignIn();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      banner={
        banner && (
          <AlertBanner>
            {banner.title && <span className="font-semibold">{banner.title} </span>}
            {banner.message}
          </AlertBanner>
        )
      }
    >
      <form onSubmit={handleSignIn} className="flex w-full max-w-[350px] flex-col">
        <AuthTitle>Log in to Unbox</AuthTitle>
        <div className="mt-7 flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            placeholder="example@email.com"
            value={username}
            onChange={setUsername}
            isRequired
            hideRequiredIndicator
            isDisabled={loading}
            autoComplete="username"
          />
          <PasswordInput
            label="Password"
            value={password}
            onChange={setPassword}
            isRequired
            hideRequiredIndicator
            isDisabled={loading}
            autoComplete="current-password"
          />
        </div>
        {error && (
          <p role="alert" className="mt-4 text-sm text-error-600">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-6 w-full" isLoading={loading}>
          Log in
        </Button>
        <AuthLinkButton className="mt-5" onClick={() => navigate({ to: "/auth/forgot-password" })} isDisabled={loading}>
          Forgot Password?
        </AuthLinkButton>
        <a
          href="https://status.unbox.social"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed right-8 bottom-8 inline-flex items-center gap-1 text-[13px] font-medium text-gray-500 hover:text-gray-700"
        >
          System status
          <LinkExternal01 className="size-3.5" />
        </a>
      </form>
    </AuthSplitLayout>
  );
};
